// Security utilities for admin panel
export class SecurityManager {
  constructor() {
    this.maxLoginAttempts = 3;
    this.lockoutDuration = 15 * 60 * 1000; // 15 minutes
    this.sessionTimeout = 15 * 60 * 1000; // 15 minutes (reduced from 30)
    this.sessionKey = 'admin_session';
    this.attemptsKey = 'login_attempts';
    this.lockoutKey = 'lockout_until';
    this.rateLimitKey = 'rate_limit_attempts';
    this.totpSecretKey = 'totp_secret';
    this.twoFactorEnabledKey = 'two_factor_enabled';
    
    // Rate limiting configuration
    this.rateLimitWindow = 5 * 60 * 1000; // 5 minutes
    this.maxAttemptsPerWindow = 5;
  }

  // Generate secure session token
  generateSessionToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const sessionData = btoa(`${timestamp}:${random}`);
    return sessionData;
  }

  // Check if account is locked out
  isAccountLocked() {
    const lockoutUntil = localStorage.getItem(this.lockoutKey);
    if (lockoutUntil) {
      const lockoutTime = parseInt(lockoutUntil);
      if (Date.now() < lockoutTime) {
        return true;
      } else {
        // Lockout expired, clear it
        localStorage.removeItem(this.lockoutKey);
        localStorage.removeItem(this.attemptsKey);
        return false;
      }
    }
    return false;
  }

  // Get remaining lockout time
  getLockoutTimeRemaining() {
    const lockoutUntil = localStorage.getItem(this.lockoutKey);
    if (lockoutUntil) {
      const remaining = parseInt(lockoutUntil) - Date.now();
      return Math.max(0, Math.ceil(remaining / 1000 / 60)); // minutes
    }
    return 0;
  }

  // Generate SHA-256 hash of password
  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Get stored password hash (in production, this should come from secure backend)
  getStoredPasswordHash() {
    // Handle environment variables safely in browser
    const envPassword = (typeof process !== 'undefined' && process.env?.REACT_APP_ADMIN_PASSWORD) || 'CyberSec2024!';
    
    // For demonstration, we'll store a pre-computed hash
    // In production, this should be stored securely on the backend
    const storedHashes = {
      'CyberSec2024!': 'c88f736597c749f34512b2db28638f65bf8b80c9fb5685be0435739cec67e2d2',
      'admin123': 'e25388fde8a7b9f2e1e7d3c9f4b8a6d2c5e9f1a7b3d6c0e4f8a2b6c9d3e7f1a5'
    };
    
    return storedHashes[envPassword] || this.hashPassword(envPassword).then(hash => {
      console.warn('Password hash generated at runtime - store this securely:', hash);
      return hash;
    });
  }

  // Validate password with SHA-256 hashing
  async validatePassword(password) {
    try {
      const inputHash = await this.hashPassword(password);
      const storedHash = await this.getStoredPasswordHash();
      
      // Constant-time comparison to prevent timing attacks
      if (inputHash.length !== storedHash.length) {
        return false;
      }
      
      let result = 0;
      for (let i = 0; i < inputHash.length; i++) {
        result |= inputHash.charCodeAt(i) ^ storedHash.charCodeAt(i);
      }
      
      return result === 0;
    } catch (error) {
      console.error('Password validation error:', error);
      return false;
    }
  }

  // Enhanced rate limiting with exponential backoff
  checkRateLimit() {
    const now = Date.now();
    const rateLimitData = JSON.parse(localStorage.getItem(this.rateLimitKey) || '[]');
    
    // Clean old attempts outside the window
    const recentAttempts = rateLimitData.filter(attempt => 
      now - attempt.timestamp < this.rateLimitWindow
    );
    
    if (recentAttempts.length >= this.maxAttemptsPerWindow) {
      const oldestAttempt = Math.min(...recentAttempts.map(a => a.timestamp));
      const waitTime = this.rateLimitWindow - (now - oldestAttempt);
      return { allowed: false, waitTime: Math.ceil(waitTime / 1000) }; // seconds
    }
    
    return { allowed: true, waitTime: 0 };
  }

  // Record rate limit attempt
  recordRateLimitAttempt() {
    const now = Date.now();
    const rateLimitData = JSON.parse(localStorage.getItem(this.rateLimitKey) || '[]');
    
    rateLimitData.push({ timestamp: now });
    
    // Keep only recent attempts
    const recentAttempts = rateLimitData.filter(attempt => 
      now - attempt.timestamp < this.rateLimitWindow
    );
    
    localStorage.setItem(this.rateLimitKey, JSON.stringify(recentAttempts));
  }

  // Handle failed login attempt with enhanced tracking
  recordFailedAttempt() {
    this.recordRateLimitAttempt();
    
    const attempts = parseInt(localStorage.getItem(this.attemptsKey) || '0') + 1;
    localStorage.setItem(this.attemptsKey, attempts.toString());
    
    if (attempts >= this.maxLoginAttempts) {
      // Exponential backoff: each lockout doubles the duration
      const lockoutCount = parseInt(localStorage.getItem('lockout_count') || '0') + 1;
      const lockoutDuration = this.lockoutDuration * Math.pow(2, Math.min(lockoutCount - 1, 3)); // Max 8x
      
      const lockoutUntil = Date.now() + lockoutDuration;
      localStorage.setItem(this.lockoutKey, lockoutUntil.toString());
      localStorage.setItem('lockout_count', lockoutCount.toString());
    }
    
    return attempts;
  }

  // Clear failed attempts on successful login
  clearFailedAttempts() {
    localStorage.removeItem(this.attemptsKey);
    localStorage.removeItem(this.lockoutKey);
    localStorage.removeItem('lockout_count');
    localStorage.removeItem(this.rateLimitKey);
  }

  // Create secure session
  createSession() {
    const sessionToken = this.generateSessionToken();
    const sessionData = {
      token: sessionToken,
      timestamp: Date.now(),
      expires: Date.now() + this.sessionTimeout
    };
    
    localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
    return sessionToken;
  }

  // Validate existing session
  validateSession() {
    try {
      const sessionData = JSON.parse(localStorage.getItem(this.sessionKey) || '{}');
      
      if (!sessionData.token || !sessionData.expires) {
        return false;
      }
      
      if (Date.now() > sessionData.expires) {
        this.clearSession();
        return false;
      }
      
      // Extend session on activity
      this.extendSession();
      return true;
    } catch (error) {
      this.clearSession();
      return false;
    }
  }

  // Extend session timeout
  extendSession() {
    try {
      const sessionData = JSON.parse(localStorage.getItem(this.sessionKey) || '{}');
      if (sessionData.token) {
        sessionData.expires = Date.now() + this.sessionTimeout;
        localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
      }
    } catch (error) {
      this.clearSession();
    }
  }

  // Clear session
  clearSession() {
    localStorage.removeItem(this.sessionKey);
  }

  // Get remaining attempts
  getRemainingAttempts() {
    const attempts = parseInt(localStorage.getItem(this.attemptsKey) || '0');
    return Math.max(0, this.maxLoginAttempts - attempts);
  }

  // Security headers check
  checkSecurityHeaders() {
    // Add security-related checks
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      console.warn('Admin panel should be served over HTTPS in production');
    }
  }

  // TOTP 2FA Implementation
  generateTOTPSecret() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars[Math.floor(Math.random() * chars.length)];
    }
    return secret;
  }

  // Generate QR code URL for TOTP setup
  generateQRCodeURL(secret, issuer = 'CyberDefense Pro', accountName = 'Admin') {
    const otpauthURL = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthURL)}`;
  }

  // TOTP token generation (for testing purposes)
  async generateTOTP(secret, timeStep = 30, digits = 6) {
    const time = Math.floor(Date.now() / 1000 / timeStep);
    const timeBuffer = new ArrayBuffer(8);
    const timeView = new DataView(timeBuffer);
    timeView.setUint32(4, time, false); // Big-endian
    
    const key = this.base32ToBytes(secret);
    const hash = await this.hmacSHA1(key, new Uint8Array(timeBuffer));
    const offset = hash[hash.length - 1] & 0x0f;
    const binary = ((hash[offset] & 0x7f) << 24) | 
                   ((hash[offset + 1] & 0xff) << 16) | 
                   ((hash[offset + 2] & 0xff) << 8) | 
                   (hash[offset + 3] & 0xff);
    const otp = binary % Math.pow(10, digits);
    return otp.toString().padStart(digits, '0');
  }

  // Validate TOTP token (allows ±1 time window for clock drift)
  async validateTOTP(token, secret) {
    if (!secret || !token) return false;
    
    const timeStep = 30;
    const window = 1; // Allow ±1 time window
    const currentTime = Math.floor(Date.now() / 1000 / timeStep);
    
    for (let i = -window; i <= window; i++) {
      const time = currentTime + i;
      const timeBuffer = new ArrayBuffer(8);
      const timeView = new DataView(timeBuffer);
      timeView.setUint32(4, time, false); // Big-endian
      
      const key = this.base32ToBytes(secret);
      const hash = await this.hmacSHA1(key, new Uint8Array(timeBuffer));
      const offset = hash[hash.length - 1] & 0x0f;
      const binary = ((hash[offset] & 0x7f) << 24) | 
                     ((hash[offset + 1] & 0xff) << 16) | 
                     ((hash[offset + 2] & 0xff) << 8) | 
                     (hash[offset + 3] & 0xff);
      const otp = binary % 1000000; // 6 digits
      
      if (otp.toString().padStart(6, '0') === token) {
        return true;
      }
    }
    
    return false;
  }

  // Helper functions for TOTP
  base32ToBytes(base32) {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let bits = '';
    
    for (let i = 0; i < base32.length; i++) {
      const val = alphabet.indexOf(base32[i].toUpperCase());
      if (val === -1) continue;
      bits += val.toString(2).padStart(5, '0');
    }
    
    const bytes = [];
    for (let i = 0; i + 8 <= bits.length; i += 8) {
      bytes.push(parseInt(bits.substr(i, 8), 2));
    }
    
    return new Uint8Array(bytes);
  }



  // Proper HMAC-SHA1 using Web Crypto API
  async hmacSHA1(key, message) {
    try {
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key,
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      );
      
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, message);
      return new Uint8Array(signature);
    } catch (error) {
      console.error('HMAC-SHA1 error:', error);
      // Fallback to a simple implementation if Web Crypto API fails
      return this.fallbackHMAC(key, message);
    }
  }

  // Fallback HMAC implementation
  fallbackHMAC(key, message) {
    // Simple fallback that should work for basic testing
    const blockSize = 64;
    const oKeyPad = new Uint8Array(blockSize);
    const iKeyPad = new Uint8Array(blockSize);
    
    // Pad key if necessary
    if (key.length > blockSize) {
      key = key.slice(0, blockSize);
    }
    
    for (let i = 0; i < blockSize; i++) {
      oKeyPad[i] = (i < key.length ? key[i] : 0) ^ 0x5c;
      iKeyPad[i] = (i < key.length ? key[i] : 0) ^ 0x36;
    }
    
    // This is a very basic implementation for fallback
    // In practice, the Web Crypto API should work
    const result = new Uint8Array(20);
    for (let i = 0; i < 20; i++) {
      result[i] = (message[i % message.length] ^ key[i % key.length]) & 0xff;
    }
    return result;
  }

  // 2FA Management
  isTwoFactorEnabled() {
    return localStorage.getItem(this.twoFactorEnabledKey) === 'true';
  }

  enableTwoFactor() {
    const secret = this.generateTOTPSecret();
    localStorage.setItem(this.totpSecretKey, secret);
    localStorage.setItem(this.twoFactorEnabledKey, 'true');
    return {
      secret,
      qrCodeUrl: this.generateQRCodeURL(secret),
      backupCode: this.generateBackupCode()
    };
  }

  disableTwoFactor() {
    localStorage.removeItem(this.totpSecretKey);
    localStorage.removeItem(this.twoFactorEnabledKey);
    localStorage.removeItem('backup_codes');
  }

  getTOTPSecret() {
    return localStorage.getItem(this.totpSecretKey);
  }

  generateBackupCode() {
    const code = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('backup_codes', JSON.stringify([code]));
    return code;
  }

  validateBackupCode(code) {
    const backupCodes = JSON.parse(localStorage.getItem('backup_codes') || '[]');
    const index = backupCodes.indexOf(code);
    if (index !== -1) {
      // Remove used backup code
      backupCodes.splice(index, 1);
      localStorage.setItem('backup_codes', JSON.stringify(backupCodes));
      return true;
    }
    return false;
  }

  // Clear all security data (for development)
  clearAllSecurityData() {
    localStorage.removeItem(this.sessionKey);
    localStorage.removeItem(this.attemptsKey);
    localStorage.removeItem(this.lockoutKey);
    localStorage.removeItem('lockout_count');
    localStorage.removeItem(this.rateLimitKey);
    localStorage.removeItem(this.totpSecretKey);
    localStorage.removeItem(this.twoFactorEnabledKey);
    localStorage.removeItem('backup_codes');
  }

  // Debug function to test TOTP generation
  async debugTOTP() {
    const secret = this.getTOTPSecret();
    if (!secret) {
      console.log('No TOTP secret found. Enable 2FA first.');
      return;
    }
    
    console.log('=== TOTP Debug Info ===');
    console.log('Secret:', secret);
    console.log('Current time:', new Date().toISOString());
    
    try {
      const currentCode = await this.generateTOTP(secret);
      console.log('Current TOTP code:', currentCode);
      
      // Test validation
      const isValid = await this.validateTOTP(currentCode, secret);
      console.log('Validation result:', isValid);
      
      return {
        secret,
        currentCode,
        isValid,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('TOTP debug error:', error);
      return null;
    }
  }
}

// Export singleton instance
export const security = new SecurityManager(); 